import {
  apiRequest,
  Output,
  Topic
  // useDatadog,
  // useTextStream,
} from "@vojomo-core/shared";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { usePermissions } from "./use-permissions";
import { useNavigate } from "react-router-dom";

export const useTopicProvider = (topicId: string, readOnly: boolean) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("");
  // const { startStreaming, streamedText, isStreaming } = useTextStream();

  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [streamedText, setStreamedText] = useState<string>("");

  const [currentOutputText, setCurrentOutputText] = useState<string>("");
  const [selectedSources, setSelectedSources] = useState<string[]>([]);

  const [outputsList, setOutputsList] = useState<string[]>([]);
  const [topicType, setTopicType] = useState<string>(null);
  const [topicTitle, setTopicTitle] = useState<string>(null);
  const [peakAmbitionDate, setPeakAmbitionDate] = useState<string>(null);
  const [roadmapViewMode, setRoadmapViewMode] = useState<"visual" | "table">(
    "visual"
  );

  const [isGeneratingOutput, setIsGeneratingOutput] = useState<boolean>(false);
  const [isLoadingTopic, setIsLoadingTopic] = useState<boolean>(false);
  const [isLoadingOutput, setIsLoadingOutput] = useState<boolean>(false);

  const [isTopicTypeModalVisible, setIsTopicTypeModalVisible] = useState(false);
  const [changedDocType, setChangedDocType] = useState<string | null>(null);

  const [coverImage, setCoverImage] = useState<{
    public_url: string;
    is_generating: boolean;
    count: number;
  }>(undefined);

  const [generatingCoverImage, setGeneratingCoverImage] =
    useState<boolean>(false);
  const [uploadingCoverImage, setUploadingCoverImage] =
    useState<boolean>(false);

  const [isTitleInputFocused, setIsTitleInputFocused] =
    useState<boolean>(false);

  const [isLoadingImage, setIsLoadingImage] = useState<boolean>(true);

  const [isTitleTyping, setIsTitleTyping] = useState<boolean>(false);

  const refreshImage = async (
    uploading: boolean = false,
    forceTopicId = topicId
  ) => {
    !uploading ? setGeneratingCoverImage(true) : setUploadingCoverImage(true);

    const image = await apiRequest<{
      public_url: string;
      is_generating: boolean;
      count: number;
    }>({
      subRoute: "topic",
      path: `/get_image?topic_id=${forceTopicId}`
    });

    setCoverImage(image);

    if (image?.is_generating) {
      setTimeout(refreshImage, 5000);
    } else {
      !uploading
        ? setGeneratingCoverImage(false)
        : setUploadingCoverImage(false);
    }

    setIsLoadingImage(false);
  };

  const [versionIndex, setVersionIndex] = useState(-1);
  const [subObjectiveIndex, setSubObjectiveIndex] = useState(0);

  const [output, setOutput] = useState<Output>(null);

  const [topic, setTopic] = useState<Topic>(undefined);

  // const topic: Topic = useMemo(() => {
  //   if (parentTopic === undefined || parentTopic === null) {
  //     return parentTopic;
  //   }

  //   //issue #537 disable sub-topics
  //   // if (parentTopic.sub_topics?.length > 0) {
  //   //   return parentTopic.sub_topics[subObjectiveIndex];
  //   // }

  //   return parentTopic;
  // }, [parentTopic, subObjectiveIndex]);

  const { permissions, hasReadAccess, hasWriteAccess } = usePermissions(
    topicId,
    "topic"
  );

  const updateDocType = async (docType: string) => {
    await apiRequest({
      subRoute: "topic",
      path: `/update_doc_type`,
      method: "POST",
      body: { topicId: topic["_id"], docType }
    });

    setTopicType(docType);
  };

  const [topicTypeOpen, setTopicTypeOpen] = useState(false);

  const refreshOutput = useCallback(async () => {
    setIsLoadingOutput(true);
    if (
      outputsList.length > 0 &&
      versionIndex >= 0 &&
      versionIndex < outputsList.length
    ) {
      const data = await apiRequest<Output>({
        subRoute: "topic",
        path: `/get_output?output_id=${outputsList[versionIndex]}`
      });

      setOutput(data);
    } else {
      setOutput(null);
    }

    setIsLoadingOutput(false);
  }, [outputsList, versionIndex]);

  const refreshTopic = useCallback(
    async (forceTopicId: string = topicId) => {
      setIsLoadingTopic(true);
      setOutputsList([]);
      if (forceTopicId && hasReadAccess) {
        const data = await apiRequest<Topic>({
          subRoute: "topic",
          path: `/get_topic?topic_id=${forceTopicId}`
        });

        setTopic(data);
      }
      setIsLoadingTopic(false);
    },
    [topicId, hasReadAccess]
  );

  useEffect(() => {
    if (topic) {
      setTopicType(topic.type || "");
      setOutputsList((topic.outputs as string[]) || []);
      setSelectedSources(topic.source_ids || []);
      setTopicTitle(topic.title || "");
    } else {
      setTopicType(null);
      setOutputsList([]);
      setSelectedSources([]);
      setTopicTitle(null);
    }
  }, [topic]);

  useEffect(() => {
    if (outputsList.length > 0) {
      setVersionIndex(outputsList.length - 1);
    } else {
      setVersionIndex(-1);
    }
  }, [outputsList]);

  const versionsCount = useMemo(() => {
    return outputsList.length;
  }, [outputsList]);

  const isLatestVersion = useMemo(() => {
    return versionIndex == versionsCount - 1;
  }, [versionIndex, versionsCount]);

  useEffect(() => {
    refreshTopic();
  }, [refreshTopic]);

  useEffect(() => {
    refreshOutput();
  }, [refreshOutput]);

  useEffect(() => {
    if (topicId) {
      refreshImage();
    }
  }, [topicId]);

  const createNewTopicType = async (newTopicType: string) => {
    setIsLoadingTopic(true);
    // Create new topic
    const newTopicId = await apiRequest<string>({
      method: "POST",
      subRoute: "ai",
      path: "/create_new_topic_from_source",
      body: {
        sourceTopicId: topicId,
        newTopicType
      }
    });

    navigate(`/documents/docs/${newTopicId}`);
    setChangedDocType(null);
    setIsLoadingTopic(false);
  };

  const updateTitle = async ({
    topicId,
    title
  }: {
    topicId: string;
    title: string;
  }) => {
    await apiRequest({
      subRoute: "topic",
      method: "POST",
      path: "/update_title",
      body: {
        topicId,
        title
      }
    });

    setTopicTitle(title);
  };

  const deleteOutput = async () => {
    setIsGeneratingOutput(true);

    await apiRequest({
      subRoute: "topic",
      path: `/delete_output`,
      method: "POST",
      body: { topicId: topic["_id"], outputId: output["_id"] }
    });

    await refreshTopic();

    setIsGeneratingOutput(false);
  };

  const [docListModalVisible, setDocListModalVisible] = useState(false);

  const generateCoverImage = async () => {
    setGeneratingCoverImage(true);

    await apiRequest({
      subRoute: "ai",
      path: "/generate_cover_image",
      method: "POST",
      body: {
        topicId: topic["_id"]
      }
    });

    refreshImage();
  };

  useEffect(() => {
    if (output) {
      setCurrentOutputText(output.text);
    } else {
      setCurrentOutputText("");
    }
  }, [output]);

  return {
    topic,
    topicId,

    output,
    setOutput,

    isGeneratingOutput,
    isLoadingOutput,

    versionsCount,
    versionIndex,
    setVersionIndex,

    deleteOutput,

    coverImage,
    setCoverImage,

    hasReadAccess,
    hasWriteAccess: hasWriteAccess && !readOnly,

    isLatestVersion,

    refreshImage,
    generateCoverImage,

    generatingCoverImage,
    setGeneratingCoverImage,
    uploadingCoverImage,

    isStreaming,
    setIsStreaming,

    streamedText,
    setStreamedText,
    refreshTopic,
    updateDocType,

    topicTypeOpen,
    setTopicTypeOpen,

    activeTab,
    setActiveTab,

    isTitleInputFocused,
    setIsTitleInputFocused,

    isTitleTyping,
    setIsTitleTyping,

    updateTitle,

    subObjectiveIndex,
    setSubObjectiveIndex,

    permissions,

    docListModalVisible,
    setDocListModalVisible,

    selectedSources,
    setSelectedSources,

    currentOutputText,
    setCurrentOutputText,

    isLoadingTopic,

    outputsList,
    setOutputsList,

    topicType,
    setTopicType,

    topicTitle,
    setTopicTitle,

    peakAmbitionDate,
    setPeakAmbitionDate,

    isLoadingImage,

    createNewTopicType,

    isTopicTypeModalVisible,
    setIsTopicTypeModalVisible,

    changedDocType,
    setChangedDocType,

    roadmapViewMode,
    setRoadmapViewMode
  };
};

export const TopicProvider = ({ children, readOnly, topicId }) => {
  const topicProvider = useTopicProvider(topicId, readOnly);

  return (
    <TopicContext.Provider value={topicProvider}>
      {children}
    </TopicContext.Provider>
  );
};

export type TopicProps = ReturnType<typeof useTopicProvider>;
export const TopicContext = createContext<TopicProps>(null);
export const useTopic = () => useContext(TopicContext);
